import { createApi } from "@reduxjs/toolkit/query/react";
import { customBaseQuery } from "../../../utils/general";

export const communityApiSlice = createApi({
  reducerPath: "communityApi",
  tagTypes: ["Community", "MyCommunities", "Memberships", "CommunityContent", "DiscussionComments", "CommunityAdmins"],
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    // Get all communities with filters
    getCommunities: builder.query({
      query: ({ search, category, state, featured, page = 1, limit = 12 } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        if (category) params.append("category", category);
        if (state) params.append("state", state);
        if (featured) params.append("featured", featured);
        params.append("page", page);
        params.append("limit", limit);
        return {
          url: `/communities?${params.toString()}`,
        };
      },
      providesTags: ["Community"],
    }),

    // Get featured communities
    getFeaturedCommunities: builder.query({
      query: () => ({
        url: `/communities/featured`,
      }),
      providesTags: ["Community"],
    }),

    // Get community categories
    getCommunityCategories: builder.query({
      query: () => ({
        url: `/communities/categories`,
      }),
    }),

    // Get single community by ID
    // Optionally pass walletAddress for anonymous wallet users to check membership
    getCommunityById: builder.query({
      query: ({ id, walletAddress }) => {
        const params = new URLSearchParams();
        if (walletAddress) params.append("walletAddress", walletAddress);
        const queryString = params.toString();
        return {
          url: `/communities/${id}${queryString ? `?${queryString}` : ""}`,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "Community", id: result?._id },
        "Community",
      ],
    }),

    // Get community members
    getCommunityMembers: builder.query({
      query: ({ id, page = 1, limit = 20 }) => ({
        url: `/communities/${id}/members?page=${page}&limit=${limit}`,
      }),
      providesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
      ],
    }),

    // Get communities created by current user
    getMyCommunities: builder.query({
      query: () => ({
        url: `/communities/user/my-communities`,
      }),
      providesTags: ["MyCommunities"],
    }),

    // Get communities where user is a member
    getMyMemberships: builder.query({
      query: () => ({
        url: `/communities/user/memberships`,
      }),
      providesTags: ["Memberships"],
    }),

    // Save community as draft
    saveCommunity: builder.mutation({
      query: ({ body }) => ({
        url: `/communities/save`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyCommunities", "Community"],
    }),

    // Edit community
    editCommunity: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/edit/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "MyCommunities",
        "Community",
      ],
    }),

    // Publish community
    publishCommunity: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/publish/${id}`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "MyCommunities",
        "Community",
      ],
    }),

    // Upload metadata to IPFS
    uploadCommunityMetadata: builder.mutation({
      query: ({ id }) => ({
        url: `/communities/${id}/metadata`,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
      ],
    }),

    // Update deployment status
    updateDeploymentStatus: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/deployment-status`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
      ],
    }),

    // Verify minting transaction
    verifyMintTransaction: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/verify-mint`,
        method: "POST",
        body,
      }),
    }),

    // Get community status
    getCommunityStatus: builder.query({
      query: ({ id }) => ({
        url: `/communities/${id}/status`,
      }),
      providesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
      ],
    }),

    // Record NFT purchase
    recordCommunityPurchase: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/purchase`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "Memberships",
        "Community",
      ],
    }),

    // Claim free membership (premint mode, price = 0, requires FunDeSci profile)
    claimFreeMembership: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/claim-free`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "Memberships",
        "Community",
      ],
    }),

    // Sync membership from blockchain (PUBLIC - no auth required)
    // Used to recover when session expired during NFT purchase
    syncMembershipFromChain: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/sync-membership`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "Memberships",
        "Community",
      ],
    }),

    // Verify membership by checking on-chain NFT ownership
    verifyMembership: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/verify-membership`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "Memberships",
      ],
    }),

    // Like community
    likeCommunity: builder.mutation({
      query: ({ id }) => ({
        url: `/communities/like/${id}`,
        method: "POST",
      }),
      // Don't invalidate to prevent unnecessary refetch
      invalidatesTags: [],
    }),

    // Dislike community
    dislikeCommunity: builder.mutation({
      query: ({ id }) => ({
        url: `/communities/dislike/${id}`,
        method: "POST",
      }),
      // Don't invalidate to prevent unnecessary refetch
      invalidatesTags: [],
    }),

    // Change community visibility
    changeCommunityVisibility: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/visibility`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Community", id: arg.id },
        "MyCommunities",
      ],
    }),

    // Delete community
    deleteCommunity: builder.mutation({
      query: ({ id }) => ({
        url: `/communities/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["MyCommunities", "Community"],
    }),

    // ==========================================
    // Premium Content Endpoints (Token-Gated)
    // ==========================================

    // Get community premium content
    getCommunityContent: builder.query({
      query: ({ id, type, page = 1, limit = 20, walletAddress }) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        params.append("page", page);
        params.append("limit", limit);
        if (walletAddress) params.append("walletAddress", walletAddress);
        return {
          url: `/communities/${id}/content?${params.toString()}`,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Create new content (creator only)
    createCommunityContent: builder.mutation({
      query: ({ id, body }) => ({
        url: `/communities/${id}/content`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Update content (creator only)
    updateCommunityContent: builder.mutation({
      query: ({ id, contentId, body }) => ({
        url: `/communities/${id}/content/${contentId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Delete content (creator only)
    deleteCommunityContent: builder.mutation({
      query: ({ id, contentId }) => ({
        url: `/communities/${id}/content/${contentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Like content (members only)
    likeCommunityContent: builder.mutation({
      query: ({ id, contentId, body }) => ({
        url: `/communities/${id}/content/${contentId}/like`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Vote on poll content (members only)
    votePollContent: builder.mutation({
      query: ({ id, contentId, optionIndex }) => ({
        url: `/communities/${id}/content/${contentId}/vote`,
        method: "POST",
        body: { optionIndex },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // ==========================================
    // Discussion Comments
    // ==========================================

    // Get comments for a discussion post (members only)
    getDiscussionComments: builder.query({
      query: ({ id, contentId, page = 1, limit = 50, walletAddress }) => {
        const params = new URLSearchParams();
        params.append("page", page);
        params.append("limit", limit);
        if (walletAddress) params.append("walletAddress", walletAddress);
        return {
          url: `/communities/${id}/content/${contentId}/comments?${params.toString()}`,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "DiscussionComments", id: arg.contentId },
      ],
    }),

    // Add a comment to a discussion post (members only)
    addDiscussionComment: builder.mutation({
      query: ({ id, contentId, text, walletAddress }) => ({
        url: `/communities/${id}/content/${contentId}/comments`,
        method: "POST",
        body: { text, walletAddress },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "DiscussionComments", id: arg.contentId },
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // Delete a comment (author or creator)
    deleteDiscussionComment: builder.mutation({
      query: ({ id, contentId, commentId }) => ({
        url: `/communities/${id}/content/${contentId}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "DiscussionComments", id: arg.contentId },
        { type: "CommunityContent", id: arg.id },
      ],
    }),

    // ==========================================
    // Community Admin Management
    // ==========================================

    // Get community admins (creator only)
    getCommunityAdmins: builder.query({
      query: ({ id }) => ({
        url: `/communities/${id}/admins`,
      }),
      providesTags: (result, error, arg) => [
        { type: "CommunityAdmins", id: arg.id },
      ],
    }),

    // Search user by email for admin invitation
    searchAdminByEmail: builder.mutation({
      query: ({ id, email }) => ({
        url: `/communities/${id}/admins/search`,
        method: "POST",
        body: { email },
      }),
    }),

    // Invite user as community admin
    inviteCommunityAdmin: builder.mutation({
      query: ({ id, email }) => ({
        url: `/communities/${id}/admins/invite`,
        method: "POST",
        body: { email },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityAdmins", id: arg.id },
        { type: "Community", id: arg.id },
      ],
    }),

    // Remove community admin
    removeCommunityAdmin: builder.mutation({
      query: ({ id, adminId }) => ({
        url: `/communities/${id}/admins/${adminId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "CommunityAdmins", id: arg.id },
        { type: "Community", id: arg.id },
      ],
    }),
  }),
});

export const {
  useGetCommunitiesQuery,
  useGetFeaturedCommunitiesQuery,
  useGetCommunityCategoriesQuery,
  useGetCommunityByIdQuery,
  useLazyGetCommunityByIdQuery,
  useGetCommunityMembersQuery,
  useGetMyCommunitiesQuery,
  useGetMyMembershipsQuery,
  useSaveCommunityMutation,
  useEditCommunityMutation,
  usePublishCommunityMutation,
  useUploadCommunityMetadataMutation,
  useUpdateDeploymentStatusMutation,
  useVerifyMintTransactionMutation,
  useGetCommunityStatusQuery,
  useLazyGetCommunityStatusQuery,
  useRecordCommunityPurchaseMutation,
  useClaimFreeMembershipMutation,
  useSyncMembershipFromChainMutation,
  useVerifyMembershipMutation,
  useLikeCommunityMutation,
  useDislikeCommunityMutation,
  useChangeCommunityVisibilityMutation,
  useDeleteCommunityMutation,
  // Premium Content
  useGetCommunityContentQuery,
  useLazyGetCommunityContentQuery,
  useCreateCommunityContentMutation,
  useUpdateCommunityContentMutation,
  useDeleteCommunityContentMutation,
  useLikeCommunityContentMutation,
  useVotePollContentMutation,
  // Discussion Comments
  useGetDiscussionCommentsQuery,
  useLazyGetDiscussionCommentsQuery,
  useAddDiscussionCommentMutation,
  useDeleteDiscussionCommentMutation,
  // Admin Management
  useGetCommunityAdminsQuery,
  useSearchAdminByEmailMutation,
  useInviteCommunityAdminMutation,
  useRemoveCommunityAdminMutation,
} = communityApiSlice;
